"use client";

import { useCallback, useSyncExternalStore } from "react";

import {
  DISPLAY_ZONE_STORAGE_KEY,
  displayZoneFor,
  HOST_TIME_ZONE,
  parseDisplayZonePreference,
  visitorTimeZone,
  type DisplayZonePreference,
} from "@/lib/own-booking";

/**
 * Which time zone booking times are shown in. Its own small module so /booked and the manage page
 * can use it without pulling in the whole picker.
 */

const noSubscribe = () => () => {};

/**
 * The visitor's time zone, London on the server. useSyncExternalStore rather than an effect, so
 * the server render and hydration agree and the real zone appears on the next pass.
 */
export function useVisitorTimeZone(): string {
  return useSyncExternalStore(noSubscribe, visitorTimeZone, () => HOST_TIME_ZONE);
}

/* The "UK time" or "my time" choice. Kept in localStorage so it survives a reload and carries from
   the picker to the confirmation and the manage page; held in memory too, because storage can be
   blocked (private windows, strict settings) and the switch must still work for the visit. */
const ZONE_CHANGED = "ownly-booking-time-zone-changed";
let rememberedPreference: DisplayZonePreference | null = null;

function readPreference(): DisplayZonePreference | null {
  try {
    return parseDisplayZonePreference(window.localStorage.getItem(DISPLAY_ZONE_STORAGE_KEY)) ?? rememberedPreference;
  } catch {
    return rememberedPreference;
  }
}

function subscribePreference(onChange: () => void): () => void {
  window.addEventListener(ZONE_CHANGED, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(ZONE_CHANGED, onChange);
    window.removeEventListener("storage", onChange);
  };
}

/**
 * The zone every booking time is shown in: the visitor's browser zone by default, or UK time if
 * they switched to it. London on the server, until the browser has said.
 */
export function useDisplayTimeZone(): { zone: string; browserZone: string; setZone: (zone: string) => void } {
  const browserZone = useVisitorTimeZone();
  const preference = useSyncExternalStore(subscribePreference, readPreference, () => null);
  const setZone = useCallback((zone: string) => {
    const next: DisplayZonePreference = zone === HOST_TIME_ZONE ? "uk" : "local";
    rememberedPreference = next;
    try {
      window.localStorage.setItem(DISPLAY_ZONE_STORAGE_KEY, next);
    } catch {
      /* Storage blocked: the in-memory choice still holds for this visit. */
    }
    window.dispatchEvent(new Event(ZONE_CHANGED));
  }, []);
  return { zone: displayZoneFor(preference, browserZone), browserZone, setZone };
}
